import { Component, HostListener, Inject, Input, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { Vet } from '@app/core/models';
import { Market } from '@app/core/models/market';
import { AuthService } from '@app/core/services/auth/auth.service';
import { StartFlowService } from '@app/core/services/start-flow/start-flow.service';
import { GTMService, quickLinksInteraction, topNavClickGtm, TopNavLinkGtmEnum } from '@app/core/services/tracking';
import { mapToolToLabel } from '@app/core/services/tracking/helpers/mapToolToLabel';
import { ArianeTranslationPipe, TranslatePipe } from '@app/shared/pipes';
import { Constants, DetectMobile } from '@app/shared/utils';
import { IconName } from '@app/shared/utils/icon/icons/icons/icons';
import { getMarketQuicklinks, getTranslateCivility } from '@app/shared/utils/static-helpers/vet-helper';
import { CoreFacade } from '@app/store/core/core.facade';
import { VetFacade } from '@app/store/vet';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { AppRoutes } from '@app/shared/utils/enums/routes.enum';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { DataCSmaskDirective } from '../../../shared/directives/data-cs-mask.directive';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { IconComponent } from '../../../shared/utils/icon/icon.component';
import { RcProgressBarComponent } from '@app/shared/components';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    RouterLink,
    MatTooltip,
    DataCSmaskDirective,
    RouterLinkActive,
    IconComponent,
    TitleCasePipe,
    TranslatePipe,
    RcProgressBarComponent,
    AsyncPipe,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  /**
   * Properties
   */

  @Input()
  set vet(vet: Vet) {
    this._vet = vet;
    // init vetFullName
    if (vet && typeof vet !== 'string') {
      const title = vet.user.title ? getTranslateCivility(vet.user.title) : vet.user.firstName;
      this.vetFullName = `${title} ${vet.user.lastName}`;
      if (this.locale === 'ko-KR' && !vet.user.title) {
        this.vetFullName = `${vet.user.lastName} ${title}`;
      }
    }

    // init vetImage
    if (vet && typeof vet !== 'string' && vet.user && vet.user.avatarUrl) {
      this.vetImage = vet.user.avatarUrl;
    } else {
      this.vetImage = Constants.placeholderImgVet;
    }
  }
  get vet(): Vet {
    return this._vet;
  }

  menuIsActive = false;
  isMobile = false;
  error: string;
  subHeaderLinks: any[];
  hideSubheader = false;
  vetFullName = '--';
  vetImage: string;
  _vet: Vet;

  // Dropdown products array
  shorcutsMenu: {
    iconName: IconName;
    text: string;
    module: string;
    link?: string;
    trackingKey?: TopNavLinkGtmEnum;
  }[] = [];

  partnerLinks: {
    icon: string;
    text: string;
    module: string;
    link?: string;
  }[] = [];

  public readonly IconName = IconName;
  public readonly AppRoutes = AppRoutes;

  public progressValue$ = this.coreFacade.progressValue$;

  private _destroyed$ = new Subject<void>();

  /**
   * Initializer
   */
  constructor(
    private router: Router,
    private authService: AuthService,
    private vetFacade: VetFacade,
    private _arianePipe: ArianeTranslationPipe,
    private startFlowService: StartFlowService,
    private trackingService: GTMService,
    @Inject(LOCALE_ID) protected locale: string,
    private readonly coreFacade: CoreFacade,
  ) {
    combineLatest([this.coreFacade.url$, this.router.events.pipe(startWith(false))])
      .pipe(
        filter(([_, event]) => !event || event instanceof NavigationEnd),
        filter(([url]) => !!url),
        tap(([url]) => {
          this.parsePathUrl(url);
        }),
      )
      .subscribe();
  }

  @HostListener('document:click', ['$event']) clickedOutside() {
    if (this.menuIsActive) {
      this.menuIsActive = !this.menuIsActive;
      this.removeActiveElementOnOutsideClick();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldShrink = scrollTop > 50; // Start shrinking after 50px scroll

    // Apply class to both body and header element for better CSS inheritance
    if (shouldShrink) {
      document.body.classList.add('header--scrolled');
      const headerElement = document.querySelector('.l-header');
      if (headerElement) {
        headerElement.classList.add('header--scrolled');
      }
    } else {
      document.body.classList.remove('header--scrolled');
      const headerElement = document.querySelector('.l-header');
      if (headerElement) {
        headerElement.classList.remove('header--scrolled');
      }
    }
  }

  /**
   * Life Cycle
   */
  ngOnInit() {
    this.isMobile = DetectMobile.isMobile();
    this._buildshorcutsMenu();
    this.checkHideSubheader();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Check if current route should hide subheader
   */
  private checkHideSubheader() {
    // Create an observable for route data
    const routeData$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const currentRoute = this.router.routerState.snapshot.root;
        return this.getHideSubheaderFromRoute(currentRoute);
      }),
    );

    // Combine route data with stepper items
    combineLatest([this.coreFacade.stepperItems$, routeData$])
      .pipe(takeUntil(this._destroyed$))
      .subscribe(([stepperItems, shouldHideFromRoute]) => {
        const hasStepperItems = stepperItems && stepperItems.length > 0;
        this.hideSubheader = (shouldHideFromRoute as boolean) || hasStepperItems;

        if (this.hideSubheader) {
          document.body.classList.add('hideSubHeader');
        } else {
          document.body.classList.remove('hideSubHeader');
        }
      });
  }

  /**
   * Recursively check route data for hideSubheader property
   */
  private getHideSubheaderFromRoute(route: any): boolean {
    if (route?.data?.hideSubheader) {
      return true;
    }

    if (route?.children) {
      for (const child of route.children) {
        if (this.getHideSubheaderFromRoute(child)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Show normal size dropdown menu or full size
   * @param event - Click or Touch event
   */
  showDropdownMenu(event) {
    if (DetectMobile.isMobile()) {
      this.dropdownMenuLowres(event);
    } else {
      this.dropDownMenuHires(event);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.isMobile = event.target.innerWidth <= 960;
  }

  logout(event) {
    event.preventDefault();
    this.authService.logout();
  }

  onClickSubMenuItem(submenu: any) {
    if (submenu?.trackingKey) {
      this.trackingService.sendInteraction(topNavClickGtm(submenu.trackingKey));
    }
  }

  /**
   * Close dropdown when is opened on fullscreen
   */
  closeDropdown() {
    const dropdownHolderElm: HTMLElement = document.querySelector('.dropdown-holder');
    const dropDownMenuElm = document.querySelectorAll('.dropdown-section__wrap');

    if (dropdownHolderElm.classList.contains('active')) {
      document.body.classList.remove('no-scroll');
      dropdownHolderElm.classList.remove('active');
    }

    this.removeActiveClass(dropDownMenuElm);
  }

  /**
   * Show dropdown menu on fullscreen
   * @param event - Touch event on menu
   */
  private dropdownMenuLowres(event) {
    event.stopPropagation();

    const dataAttribute = event.target.getAttribute('data-content');
    const dropdownHolderElm: HTMLElement = document.querySelector('.dropdown-holder');
    const currentElm: HTMLElement = document.querySelector(`.dropdown-holder__wrap > .dropdown-section__wrap--${dataAttribute}`);

    document.body.classList.add('no-scroll');
    dropdownHolderElm.classList.add('active');
    currentElm.classList.add('active');
  }

  /**
   * Show dropdown menu on high resolution
   * @param event - Click event on menu
   */
  private dropDownMenuHires(event) {
    event.stopPropagation();

    const currentElm = event.currentTarget;
    const dataAttribute = currentElm.getAttribute('data-content');
    const headerMenu = document.querySelectorAll('.header__menu-icon');
    const allDropdowns = document.querySelectorAll('.dropdown');
    const allDropdownMenu = document.querySelectorAll('.dropdown-menu');

    const headerProfile: HTMLElement = document.querySelector('.header__menu-profile');
    const dropDown: HTMLElement = document.querySelector(`.dropdown--${dataAttribute}`);
    const dropDownMenu: HTMLElement = document.querySelector(`.dropdown-menu--${dataAttribute}`);
    const rcIconElm: HTMLElement = document.querySelector('.rc-icon--arrow-down');

    if (currentElm.classList.contains('active')) {
      currentElm.classList.remove('active');
      dropDown.classList.remove('active');
      dropDownMenu.classList.remove('active');
      rcIconElm.classList.remove('active');
      this.menuIsActive = false;

      return;
    }

    if (headerProfile.classList.contains('active')) {
      headerProfile.classList.remove('active');
    }

    if (rcIconElm.classList.contains('active')) {
      rcIconElm.classList.remove('active');
    }

    this.removeActiveClass(headerMenu);
    this.removeActiveClass(allDropdowns);
    this.removeActiveClass(allDropdownMenu);

    currentElm.classList.add('active');
    dropDown.classList.add('active');
    dropDownMenu.classList.add('active');

    if (dataAttribute === 'profile') {
      rcIconElm.classList.add('active');
    }

    this.menuIsActive = true;
  }

  /**
   * Remove all active class when use click outside
   */
  private removeActiveElementOnOutsideClick() {
    const headerMenus = document.querySelectorAll('.header__menu-icon');
    const allDropdowns = document.querySelectorAll('.dropdown');
    const rcIconElm: HTMLElement = document.querySelector('.rc-icon--arrow-down');
    const headerProfile: HTMLElement = document.querySelector('.header__menu-profile');

    headerMenus.forEach((_, index) => {
      const activeMenu: HTMLElement = headerMenus[index] as HTMLElement;

      if (activeMenu.classList.contains('active')) {
        this.removeActiveClass(headerMenus);
        this.removeActiveClass(allDropdowns);
        return;
      }
    });

    if (rcIconElm.classList.contains('active')) {
      rcIconElm.classList.remove('active');
    }

    if (headerProfile.classList.contains('active')) {
      this.removeActiveClass(allDropdowns);
    }
  }

  /**
   * Remove all active class on the menu
   * @param element - Array of menu
   */
  private removeActiveClass(element) {
    element.forEach((_, index) => {
      const elementItem: HTMLElement = element[index] as HTMLElement;

      if (elementItem.classList.contains('active')) {
        elementItem.classList.remove('active');
      }
    });
  }

  /**
   * Parse url to get section name when user navigate through the app
   * @param url - String of path url
   */
  private parsePathUrl(url: string) {
    const parsedUrl = url.includes(';') ? url.substr(0, url.indexOf(';')) : url;
    const arianeTranslation = this._arianePipe.transform(parsedUrl);
    // in case there is a path containing '/' that we want to customize his translation.
    // ex: /products/starter-kit will return [multifunction, starter-kit]
    if (arianeTranslation !== '--' && arianeTranslation && arianeTranslation[0]) {
      this.subHeaderLinks = arianeTranslation;
    } else {
      const pathUrls = parsedUrl.split('/');
      const pathRegex = /^[a-zA-Z-]+$/;

      this.subHeaderLinks = pathUrls
        .map((path) => {
          if (path !== '' && path.includes(';')) {
            path = path.substring(0, path.indexOf(';'));
          }

          return path;
        })
        .filter((path) => path !== '' && pathRegex.test(path))
        .map((path) => {
          return this._arianePipe.transform(path);
        })
        .filter((path) => path !== '--');
    }
  }

  /**
   * Sent event on GA when user select an icon inside the menu
   * @param event - Click or Touch event
   * @param menu - Single menu object
   */
  externalLink(menu) {
    if (menu.link) {
      this.trackingService.sendInteraction(quickLinksInteraction(menu.module));
    } else {
      this.startFlowService.startFlowNewPatient(menu.flowType);
      this.trackingService.sendInteraction(quickLinksInteraction(mapToolToLabel(menu.flowType)));
    }
    this.closeDropdown();
  }

  private _buildshorcutsMenu() {
    combineLatest([this.vetFacade.isRetailPriceActivated$, this.vetFacade.market$, this.vetFacade.vet$])
      .pipe(takeUntil(this._destroyed$))
      .subscribe(([activatedPrice, market, vet]: [boolean, Market, Vet]) => {
        this.shorcutsMenu = [];
        this.shorcutsMenu.push({
          iconName: IconName.address_book_2,
          link: '/pet-record',
          text: $localize`:@@sub-header-link-pet-record:`,
          module: 'pet-record',
        });
        this.shorcutsMenu.push({
          iconName: IconName.food_2,
          link: '/products/catalogue',
          text: $localize`:@@sub-header-link-products:`,
          module: 'products-catalogue',
        });
        if (activatedPrice) {
          this.shorcutsMenu.push({
            iconName: IconName.price,
            link: '/products/prices',
            text: $localize`:@@word_prices:`,
            module: 'products-prices',
            trackingKey: TopNavLinkGtmEnum.PRICES,
          });
        }
        this.shorcutsMenu.push({
          iconName: IconName.life_saver,
          link: '/support',
          text: $localize`:@@sub-header-link-support:`,
          module: 'support',
        });
        /**
         * If some links are defined in the market data,
         * we add them to the quicklinks list
         */

        if (market?.quicklinks?.length) {
          this.partnerLinks = getMarketQuicklinks(market.quicklinks, vet.user);
        }
      });
  }
}
